import { FC, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom'
import * as TokenSale from '../../contracts/master'
import { ForceFullWidth, Relative } from '../../shared_components/Hero/styled'
import { Title } from '../Home/parts'
import {
  Container,
  Outer,
  PageToggler,
  ToggleContainer,
  ToggleTitle,
} from './styles'
import Allocations from './Allocations'
import { Allocation } from './Allocations/types'
import useWeb3 from '../../hooks/useWeb3'
import { selectAccount } from '../../modules/web3/selectors'
import { getAllocations } from './Allocations/requests'
import { selectSerialisedPools } from '../../modules/listPools/selectors'
import Big from 'big.js'
import { amountFromWei, amountToWei } from '../../utils/amount'
import {
  setShouldShowDashboardHeaderGradient,
  setShowHeaderGradient,
} from '../../modules/miscUi/actions'
import { routes } from './constants'
import allRoutes from '../../router/routes'
import { RewardsAmount } from '../../shared_components/Header'
import {
  selectHeaderHeight,
  selectRewardsAmount,
} from '../../modules/miscUi/selectors'
import { TopBanner } from '../PoolDetails/parts'
import { IPool } from '../../types/reducerTypes'
import { HelmetProvider } from '../../App'
import Launches from './Launches'
import Drops from './Drops'
import Vault from './Vault'
import styled, { css } from 'styled-components'
import { MaxWidth } from '../../shared_components/Page'
import { Box } from '../../shared_components/UI/Box'
import { ethers } from 'ethers'

const { dashboard: dashboardRoutes } = allRoutes

interface Props {
  startingIndex?: number
}

const Dashboard: FC<Props> = ({ startingIndex }) => {
  const dispatch = useDispatch()

  const {
    polygonNetwork,
    ethereumNetwork,
    bscNetwork,
    poolsData,
    blastProvider,
    provider,
  } = useWeb3()

  const account = useSelector(selectAccount)
  const serialisedPools = useSelector(selectSerialisedPools)
  const headerHeight = useSelector(selectHeaderHeight)

  const { url } = useRouteMatch()

  const history = useHistory()

  const [allocations, setAllocations] = useState<Allocation[]>([])
  const [activeIndex, setActiveIndex] = useState(startingIndex ?? 0)
  const [fetchingVendings, setFetchingVendings] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [hasFetchedForAccount, setHasFetchedForAccount] = useState('')
  const [hasAwaitedWeb3ConnectionBuffer, setHasAwaitedWeb3ConnectionBuffer] =
    useState(false)
  const [labsRewards, setLabsRewards] = useState(0)

  const rewardsAmount = useSelector(selectRewardsAmount)

  const fetchAndSetAllocations = useCallback(async () => {
    setHasFetchedForAccount(account)
    setIsLoading(true)

    const formatted = await getAllocations(account, blastProvider)

    const serialisedFormattedVestingFromCMS = formatted.reduce(
      (acc: { [key: string]: Allocation }, cur: Allocation) => {
        acc[cur.poolAddress] = cur
        return acc
      },
      {},
    )

    let allPools = serialisedPools

    if (!Object.keys(serialisedPools).length)
      allPools = await poolsData.dispatchPoolsToStore('labs')

    const poolsArray = [
      ...Object.keys(allPools)
        .map((key) => allPools[key])
        .filter((pool) => !pool?.isLegacyPool),
      ...formatted.map(({ pool }) => pool),
    ]

    for (let i = 0; i < poolsArray.length; i++) {
      const pool: IPool = poolsArray[i]

      const { name, ticker, poolAddress, chainID: chainId, poolSettings } = pool

      if (!poolSettings) continue

      const { taxRate: poolTaxRate } = poolSettings

      const isValid = ethers.isAddress(poolAddress)
      const hasEnded = new Date(pool.privateSale.end).getTime() < Date.now()

      if (!isValid || !hasEnded) continue

      const dynamicMaster = new ethers.Contract(
        poolAddress,
        TokenSale.abi as ethers.InterfaceAbi,
        provider,
      )

      const stakes = await dynamicMaster
        ?.stakes(account)
        .catch((err: any) => console.error(err))

      const decimals = Number(pool?.decimals ?? 18)

      if (!stakes) continue

      const userBought = stakes[0]
      const hasClaimedRefund = Boolean(stakes?.[2])
      const hasInvested = userBought !== '0'

      if (!hasInvested) continue

      let tokensSold = await dynamicMaster?.totalTokenSold()

      if (tokensSold === '0') continue

      if (!Boolean(Number(pool?.privateSale?.price))) continue

      const sold = new Big(amountFromWei(tokensSold, decimals).toString()).div(
        pool?.privateSale.price,
      )

      const hardCap = pool?.poolSettings?.totalSupplyForSale

      if (!hardCap) continue

      const pctValHardcap = parseInt(
        String((Number(sold.toNumber()) * 100) / Number(hardCap)),
      )

      const isDiluted = pctValHardcap > 100

      const pricePerToken = Number(pool.privateSale.price)

      let refund, tokensReceived, investmentPerToken, investmentAfterDilution

      const userInvestment = Number(userBought)

      tokensReceived = userInvestment * (1 / pricePerToken)

      const supplyInWei = amountToWei(new Big(hardCap), decimals).times(
        pool?.privateSale?.price,
      )

      const ratio = isDiluted ? supplyInWei.div(tokensSold).toNumber() : 1

      if (isDiluted) {
        tokensReceived *= ratio
        investmentPerToken = tokensReceived / userInvestment
        investmentAfterDilution = userInvestment * ratio
        refund =
          (userInvestment - investmentAfterDilution) * (1 + 1 / poolTaxRate)
      }

      const tokenSaleStats = {
        tokensReceived,
        investmentPerToken,
        pricePerToken: Number(pool.privateSale.price),
        refund,
        hasClaimedRefund,
        investmentAfterDilution,
        userInvestment,
        isBNBRaise: false,
      }

      if (
        Object.keys(serialisedFormattedVestingFromCMS).includes(poolAddress)
      ) {
        const logoUrl = pool.about.logo.url
        serialisedFormattedVestingFromCMS[poolAddress] = {
          ...serialisedFormattedVestingFromCMS[poolAddress],
          ...tokenSaleStats,
          pool: {
            ...pool,
            about: {
              ...pool.about,
            },
          },
          chainId: Number(chainId),
          logoUrl,
        }
        continue
      }

      serialisedFormattedVestingFromCMS[poolAddress] = {
        chainId: Number(chainId),
        name,
        logoUrl: pool.about.logo.url,
        points: [],
        ticker,
        index: 0,
        count: 0,
        vestingContractAddress: '',
        poolAddress,
        pool,
        ...tokenSaleStats,
      }
    }

    const afterMapping = []

    for (const key in serialisedFormattedVestingFromCMS) {
      afterMapping.push(serialisedFormattedVestingFromCMS[key])
    }

    setAllocations(afterMapping)

    setLabsRewards(
      formatted.reduce((acc, { count, index }) => acc + (count - index), 0),
    )

    setIsLoading(false)
    setFetchingVendings(false)
  }, [
    account,
    polygonNetwork,
    ethereumNetwork,
    bscNetwork,
    serialisedPools,
    poolsData,
    blastProvider,
  ])

  const setBlankAllocations = useCallback(() => {
    if (!!account || !!provider) return
    setAllocations([])
    setFetchingVendings(false)
    setIsLoading(false)
    setHasAwaitedWeb3ConnectionBuffer(false)
  }, [account, provider])

  useEffect(() => {
    if (!account || !provider) {
      const timer = setTimeout(setBlankAllocations, 1000)
      return () => clearTimeout(timer)
    }
    if (hasFetchedForAccount === account) return
    setFetchingVendings(false)
    fetchAndSetAllocations()
  }, [
    provider,
    account,
    fetchAndSetAllocations,
    fetchingVendings,
    allocations,
    hasFetchedForAccount,
    hasAwaitedWeb3ConnectionBuffer,
    setBlankAllocations,
  ])

  const isVault = activeIndex === 3

  useEffect(() => {
    const setter = isVault
      ? setShouldShowDashboardHeaderGradient
      : setShowHeaderGradient
    dispatch(setter(true))
    return () => {
      dispatch(setShowHeaderGradient(false))
      dispatch(setShouldShowDashboardHeaderGradient(false))
    }
  }, [dispatch, isVault])

  const topBannerImageSrc = isVault
    ? '/img/two-dudes-radial.png'
    : '/hero-cover-dashboard.png'

  useEffect(() => {
    const allQueryParams = new URLSearchParams(window.location.search)
    const queryString = allQueryParams.toString()
    const desiredRoute = routes[activeIndex].route
    const routeFromUrl = window.location.pathname
      .split('/')
      .pop()
      ?.split('?')[0]
    if (routeFromUrl === desiredRoute) return
    history.push(
      `/dashboard/${routes[activeIndex].route}${
        queryString ? `?${queryString}` : ''
      }`,
    )
  }, [activeIndex, history, url])

  return (
    <>
      <HelmetProvider title="ZAP | Your Dashboard" />
      <ForceFullWidth>
        <TopBanner
          bgImage={topBannerImageSrc}
          top={headerHeight}
          className="top-banner"
        />
      </ForceFullWidth>
      <Relative>
        <ForceFullWidth>
          <TopBanner
            bgImage={topBannerImageSrc}
            top={headerHeight}
            className="top-banner"
          />
        </ForceFullWidth>
      </Relative>
      <Container>
        <HeaderContainer>
          <Title>Portfolio</Title>
        </HeaderContainer>
        <PageToggler>
          {routes.map(({ title, route, disabled = false }, i) => (
            <Outer pr fu aw key={`toggle-${title}`}>
              <ToggleContainer
                ac
                fu
                aw
                isActive={i === activeIndex}
                onClick={() => !disabled && setActiveIndex(i)}
                disabled={!!disabled}
              >
                <ToggleTitle>{title}</ToggleTitle>
                {route === 'labs' ? (
                  !!labsRewards ? (
                    <RewardsAmount>{labsRewards}</RewardsAmount>
                  ) : (
                    <></>
                  )
                ) : rewardsAmount > 0 ? (
                  // <RewardsAmount>{rewardsAmount}</RewardsAmount>
                  // <RewardsAmount>{rewardsAmount}</RewardsAmount>
                  <></>
                ) : (
                  <></>
                )}
              </ToggleContainer>
            </Outer>
          ))}
        </PageToggler>
        <ForceFullWidth>
          <Body solidBg={isVault}>
            <MaxWidth maxWidth={1400}>
              <Switch>
                <Route path={dashboardRoutes.labs}>
                  <Allocations
                    fetchingVendings={fetchingVendings || isLoading}
                    allocations={allocations}
                  />
                </Route>
                <Route path={dashboardRoutes.launch} component={Launches} />
                <Route path={dashboardRoutes.drops} component={Drops} />
                <Route path={dashboardRoutes.vault} component={Vault} />
                <Route
                  path={url + '/*'}
                  render={() => <Redirect to={url + dashboardRoutes.labs} />}
                />
                <Route
                  path="*"
                  render={() => <Redirect to={dashboardRoutes.labs} />}
                />
              </Switch>
            </MaxWidth>
          </Body>
        </ForceFullWidth>
      </Container>
    </>
  )
}

export default Dashboard

const Body = styled.div<{ solidBg?: boolean }>`
  z-index: 100;
  width: 100%;
  ${({ solidBg, theme }) =>
    solidBg &&
    css`
      border-top: 1px solid rgba(255, 255, 255);
      background: ${theme.semantics.zap.surface.primary};
    `}
`

export const HeaderContainer = styled(Box)`
  z-index: 10;
  margin: 96px 0 32px;
  @media screen and (max-width: 1000px) {
    margin: 16px 0;
  }
`
